/**
 * Hook + toggle pour le theme (dark/light).
 * Persiste dans localStorage et applique data-theme sur <html>.
 *
 * Le suffixe `-v2` invalide les preferences enregistrees avant le bump :
 * tous les utilisateurs repartent en dark par defaut (et peuvent toujours
 * basculer en light via le ThemeToggle, choix qui sera memorise).
 */
import { useEffect, useState } from 'react';

const STORAGE_KEY = 'stella-theme-v2';
const LEGACY_STORAGE_KEY = 'stella-theme';
export type Theme = 'dark' | 'light';

function applyTheme(t: Theme) {
  const root = document.documentElement;
  if (t === 'light') root.setAttribute('data-theme', 'light');
  else root.removeAttribute('data-theme');
}

export function getInitialTheme(): Theme {
  try {
    // Nettoyage de l'ancienne cle pour eviter qu'elle ne reprenne le dessus
    // si on revient un jour sur la cle d'origine.
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;
    if (stored === 'light' || stored === 'dark') return stored;
  } catch { /* ignore */ }
  return 'dark';
}

export function useTheme(): { theme: Theme; setTheme: (t: Theme) => void; toggle: () => void } {
  const [theme, setThemeState] = useState<Theme>(() => getInitialTheme());

  useEffect(() => {
    applyTheme(theme);
    try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
  }, [theme]);

  return {
    theme,
    setTheme: setThemeState,
    toggle: () => setThemeState((t) => (t === 'dark' ? 'light' : 'dark')),
  };
}
